import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Auth Context
const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        pending2FAUser: null,
      };
    case 'SET_PENDING_2FA_USER':
      // Password/OAuth succeeded but tokens are not issued until TOTP is verified.
      // Must NOT set isAuthenticated — that triggers Timer/WS calls → 401 → /login bounce.
      return {
        ...state,
        pending2FAUser: action.payload,
        user: null,
        isAuthenticated: false,
      };
    case 'CLEAR_PENDING_2FA_USER':
      return { ...state, pending2FAUser: null };
    case 'LOGOUT':
      return { ...state, user: null, isAuthenticated: false, pending2FAUser: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    pending2FAUser: null,
    isAuthenticated: false,
    loading: true
  });

  // Token refresh function — deduplicated so concurrent 401s share one refresh attempt
  let refreshPromise = null;
  const refreshAccessToken = async () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
      
        const response = await axios.post('/api/users/refresh-token', {
          refresh_token: refreshToken
        });
      
        // Update tokens
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
        return response.data.access_token;
      } catch (error) {
        // Refresh failed, clear tokens and redirect to login — but only if we had a session.
        const hadRefreshToken = !!localStorage.getItem('refresh_token');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        delete axios.defaults.headers.common['Authorization'];
        dispatch({ type: 'LOGOUT' });
        if (hadRefreshToken) {
          window.location.href = '/login';
        }
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Don't try to refresh token for auth endpoints that intentionally run without a session
        const url = originalRequest.url || '';
        const isAuthEndpoint =
          url.includes('/api/users/login') ||
          url.includes('/api/users/register') ||
          url.includes('/api/users/complete-2fa-login') ||
          url.includes('/api/users/refresh-token');
        
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          // No session to refresh — reject quietly (e.g. stray calls during 2FA challenge)
          if (!localStorage.getItem('refresh_token')) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          
          try {
            const newToken = await refreshAccessToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }

    // Handle Microsoft OAuth redirect
    const handleRedirect = async () => {
      try {
        const { handleMicrosoftRedirect } = await import('../services/oauth');
        const result = await handleMicrosoftRedirect();
        if (result) {
          const loginResult = await oauthLogin(result);
          if (loginResult === 'pending_approval') {
            // Navigate to pending approval page
            window.location.href = '/pending-approval';
          } else if (loginResult === true) {
            // Successful login - navigate to dashboard
            window.location.href = '/dashboard';
          }
        }
      } catch (error) {
        // Handle error silently
      }
    };

    handleRedirect();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/users/me');
      dispatch({ type: 'SET_USER', payload: response.data });
    } catch (error) {
      // If 401, try to refresh token
      if (error.response?.status === 401) {
        try {
          await refreshAccessToken();
          // Retry the request
          const response = await axios.get('/api/users/me');
          dispatch({ type: 'SET_USER', payload: response.data });
        } catch (refreshError) {
          // Refresh failed, try to recover session
          try {
            await attemptSessionRecovery();
          } catch (recoveryError) {
            // Recovery failed, clear tokens
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      } else {
        // Other errors, clear tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Session recovery function
  const attemptSessionRecovery = async () => {
    try {
      // Try to get session info from localStorage
      const storedUser = localStorage.getItem('user_data');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        
        // Try to validate the session with the backend
        const response = await axios.post('/api/users/validate-session', {
          username: userData.username,
          last_login: userData.last_login
        });
        
        if (response.data.valid) {
          // Session is valid, restore tokens
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
          
          // Fetch fresh user data
          const userResponse = await axios.get('/api/users/me');
          dispatch({ type: 'SET_USER', payload: userResponse.data });
          
          toast.success('Session restored successfully!');
          return true;
        }
      }
      throw new Error('No valid session data found');
    } catch (error) {
      console.error('Session recovery failed:', error);
      throw error;
    }
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/users/login', credentials);

      // Check if user requires approval (similar to OAuth flow)
      if (response.data.requires_approval) {
        // For pending approval users, store user data temporarily
        dispatch({ type: 'SET_USER', payload: response.data.user });
        toast.success('Login successful! Awaiting admin approval.');
        return 'pending_approval';
      }

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        // Store username/profile for the challenge UI only — no session tokens yet
        dispatch({ type: 'SET_PENDING_2FA_USER', payload: response.data.user });
        return 'requires_2fa';
      }

      // Check if 2FA setup is suggested (first-time login)
      if (response.data.suggest_2fa) {
        // Store user data and tokens for 2FA setup
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        dispatch({ type: 'SET_USER', payload: response.data.user });
        return 'suggest_2fa';
      }

      // Regular successful login
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      dispatch({ type: 'SET_USER', payload: response.data.user });
      
      // Store user data for session recovery
      localStorage.setItem('user_data', JSON.stringify({
        username: response.data.user.username,
        email: response.data.user.email,
        last_login: new Date().toISOString()
      }));
      
      toast.success('Login successful!');
      return true;
    } catch (error) {
      // Handle rate limit errors specifically
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        if (errorData && typeof errorData === 'object') {
          const message = errorData.message || errorData.detail || 'Too many login attempts. Please try again later.';
          
          // Just show the backend message - it already includes the time
          toast.error(message);
        } else {
          toast.error('Too many login attempts. Please try again later.');
        }
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.message || 
                           'Login failed';
        toast.error(errorMessage);
      }
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/users/register', userData);
      return response.data;
    } catch (error) {
      // Handle rate limit errors specifically
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        if (errorData && typeof errorData === 'object') {
          // Extract user-friendly message from rate limit error
          const message = errorData.message || errorData.detail || 'Too many registration attempts. Please try again later.';
          
          // Just show the backend message - it already includes the time
          toast.error(message);
        } else {
          toast.error('Too many registration attempts. Please try again later.');
        }
      } else {
        // Handle other errors - detail can be string, object (password validation), or array (422 validation)
        const detail = error.response?.data?.detail;
        const message = error.response?.data?.message || 'Registration failed';
        let errorMessage;
        if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail.map((d) => d.msg || d.message || String(d)).filter(Boolean).join('. ') || message;
        } else if (typeof detail === 'object' && detail !== null) {
          const msg = detail.message || message;
          const errors = detail.errors;
          errorMessage = errors && Array.isArray(errors) && errors.length > 0
            ? `${msg}: ${errors.join('. ')}`
            : msg;
        } else {
          errorMessage = typeof detail === 'string' ? detail : message;
        }
        toast.error(errorMessage);
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to revoke refresh tokens
      await axios.post('/api/users/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
    // Redirect to login page
    window.location.href = '/login';
  };

  const oauthLogin = async (result) => {
    if (result && result.access_token) {
      localStorage.setItem('token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`;
      dispatch({ type: 'SET_USER', payload: result.user });
      
      // Check if user needs onboarding
      if (result.suggest_2fa) {
        toast.success('Login successful! Please complete your account setup.');
        return 'suggest_2fa';
      }
      
      toast.success('Login successful!');
      return true;
    } else if (result && result.requires_approval) {
      // For pending approval users, store user data temporarily
      dispatch({ type: 'SET_USER', payload: result.user });
      toast.success('Account created! Awaiting admin approval.');
      return 'pending_approval';
    } else if (result && result.requires_2fa) {
      // Challenge pending — do not mark authenticated until complete-2fa-login
      dispatch({ type: 'SET_PENDING_2FA_USER', payload: result.user });
      toast.success('Please complete 2FA verification to continue.');
      return 'requires_2fa';
    } else if (result && result.suggest_2fa) {
      // For users who need to complete onboarding
      localStorage.setItem('token', result.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${result.access_token}`;
      dispatch({ type: 'SET_USER', payload: result.user });
      toast.success('Login successful! Please complete your account setup.');
      return 'suggest_2fa';
    }
    return false;
  };

  const complete2FALogin = async (username, code) => {
    try {
      const response = await axios.post('/api/users/complete-2fa-login', {
        username: username,
        code: code
      });

      // 2FA verification successful, complete login
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      localStorage.setItem('user_data', JSON.stringify({
        username: response.data.user.username,
        email: response.data.user.email,
        last_login: new Date().toISOString()
      }));
      dispatch({ type: 'SET_USER', payload: response.data.user });
      toast.success('2FA verification successful!');
      return true;
    } catch (error) {
      // Handle rate limit errors specifically
      if (error.response?.status === 429) {
        const errorData = error.response?.data;
        if (errorData && typeof errorData === 'object') {
          const message = errorData.message || errorData.detail || 'Too many 2FA attempts. Please try again later.';
          
          // Just show the backend message - it already includes the time
          toast.error(message);
        } else {
          toast.error('Too many 2FA attempts. Please try again later.');
        }
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.message || 
                           '2FA verification failed';
        toast.error(errorMessage);
      }
      return false;
    }
  };

  const setUser = (userData) => {
    if (typeof userData === 'function') {
      // Handle updater function pattern: setUser(prev => ({ ...prev, avatar: newAvatar }))
      const currentUser = state.user;
      const updatedUser = userData(currentUser);
      dispatch({ type: 'SET_USER', payload: updatedUser });
    } else {
      // Handle direct value: setUser(newUser)
      dispatch({ type: 'SET_USER', payload: userData });
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, oauthLogin, complete2FALogin, fetchUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
